import { ContraventionReasonTranslations, GeoZoneOperationConfigWithGracePeriod, GeoZones } from '@EcamModel/model';
import { GeoZoneOperationConfigGracePeriodWithDay } from '@EcamModel/model/GeoZoneOperationConfigGracePeriod';
import PopUpBase from '@components/PopUpBase';
import { yupResolver } from '@hookform/resolvers/yup';
import { IPopUp } from '@hooks/usePopUp';
import { Button, Stack, Typography } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { validateDatePickerPastDate, validateDatePickerValue } from '@pages/cameras/add-new';
import { convertSecondsToDate } from '@utils/functions';
import { cloneDeep, groupBy } from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { Mode } from '..';
import { getGracePeriods } from '../utils';
import { DEFAULT_MIN_GRACE_PERIOD, updateGracePeriods, ZERO_GRACE_PERIOD } from '../utils/gracePeriodUtils';
import {
    contraventionReasonEV,
    contraventionReasonNoRoadway,
    defaultGracePeriod,
    defaultOperationHours,
    FormValues,
    GeoZoneGracePeriod,
    handleConvertOperationHourConfig,
    hasInvalidGracePeriod,
} from './AddGeoZones';
import GracePeriods from './GracePeriods';
import OperationalHours from './OperationalHours';

type Props = Omit<IPopUp, 'onConfirm'> & {
    mode: Mode;
    selectedGeoZone: GeoZones;
    gracePeriodConfigs?: GeoZoneGracePeriod[];
    selectedGeoZoneConfigsId: number | string;
    onConfirm?(configs: GeoZoneOperationConfigWithGracePeriod): void;
    isLoading?: boolean;
    effectiveFrom?: Date;
    contraventionReasonId: number;
};

const schema: any = yup.object().shape({
    gracePeriods: yup.array().of(
        yup.object().shape({
            Name: yup.string().required('Name is required'),
            GracePeriod: yup
                .string()
                .required('Please enter grace periods.')
                .test('validFormat', 'Use the format: x(h) x(m) x(s).', function (value) {
                    const timeRegex =
                        /^(?:[0-9]\d*h(\s[0-9]\d*m)?(\s[0-9]\d*s)?|[0-9]\d*m(\s[0-9]\d*s)?|[0-9]\d*s|[0-9]\d*)$/;
                    if (!timeRegex.test(value)) {
                        return false;
                    }
                    const totalSeconds = getGracePeriods(value);
                    if (totalSeconds < 0) {
                        return new yup.ValidationError('Time value cannot be negative.', value, this.path);
                    }
                    return true;
                }),
        })
    ),
});

export default function PopUpEditablePeriods(props: Props) {
    const [startDate, setStartDate] = useState(moment().startOf('date'));
    const [isOverlap, setIsOverlap] = useState(false);

    const {
        watch,
        reset,
        control: _control,
        setValue: _setValue,
        handleSubmit,
        setError,
        formState: { isDirty, isValid, errors, ...formState },
    } = useForm<FormValues>({
        resolver: yupResolver(schema),
        mode: 'all',
        defaultValues: {
            operationHours: [],
            gracePeriods: [defaultGracePeriod],
        },
    });

    const setValue = _setValue as (name: string, value: any, options?: Record<string, boolean>) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const control = _control as any;

    const {
        fields: operationHours,
        update: updateOperationHours,
        append: appendOperationHours,
        remove: removeOperationHours,
    } = useFieldArray({ control, name: 'operationHours' }) as any;

    const gracePeriodForm = useFieldArray({ control, name: 'gracePeriods' }) as any;
    const isInvalidOperationHours = watch().operationHours.some(
        (item) => !!item.Status && item.TimeRanges.some((t) => !t.From || !t.To)
    );
    const isHasInValidGracePeriod = hasInvalidGracePeriod(watch().operationHours, watch().gracePeriods);
    const hasOperationHours = watch().operationHours.every((item) => !item.Status);

    const handleAddOperationHours = (data: FormValues) => {
        const groupedOperationHours = groupBy(handleConvertOperationHourConfig(operationHours), '_fakeId');

        const _data: GeoZoneOperationConfigWithGracePeriod = {
            Id: props.selectedGeoZoneConfigsId as number,
            GeoZoneId: props.selectedGeoZone.Id as number,
            EffectiveFrom: moment(startDate).startOf('date').toDate(),
            GZOConfigGracePeriods: data.gracePeriods.map(
                (item) =>
                    ({
                        ...item,
                        Id: item.Id,
                        GracePeriod: getGracePeriods(item.GracePeriod),
                        GZOConfigDays: groupedOperationHours[item._fakeId],
                    } as GeoZoneOperationConfigGracePeriodWithDay)
            ),
            Status: false,
        };
        if (props.mode === 'create') delete _data.Id;
        props.onConfirm?.(_data);
    };
    useEffect(() => {
        if (!props.open) return;

        const isZeroGracePeriod = [contraventionReasonEV, contraventionReasonNoRoadway].includes(
            props?.contraventionReasonId
        );

        if (!!props.gracePeriodConfigs?.length && props.mode === 'edit') {
            setStartDate(moment(props.effectiveFrom).startOf('date'));
            const _gracePeriods = props.gracePeriodConfigs?.map((item, index) => {
                if (index === 0) {
                    return {
                        ...item,
                        _fakeId: 'df-1',
                        GZOConfigDays: item.GZOConfigDays.map((o) => ({ ...o, _fakeId: 'df-1' })),
                    };
                }
                return {
                    ...item,
                    _fakeId: `df-${index + 1}`,
                    GZOConfigDays: item.GZOConfigDays.map((o) => ({ ...o, _fakeId: `df-${index + 1}` })),
                };
            });

            const convertedOperationHours = _gracePeriods?.flatMap((item) => item.GZOConfigDays.map((o) => o));

            const _defaultOperationHours = cloneDeep(defaultOperationHours);
            const _operationHours = _defaultOperationHours.map((day) => {
                const weekday = day.Weekday;
                const matchingConvertedHours = convertedOperationHours.filter((item) => item.Weekday === weekday);

                if (matchingConvertedHours.length > 0) {
                    day.TimeRanges = matchingConvertedHours.map(
                        (convertedOperationHour) =>
                            ({
                                Id: convertedOperationHour.Id,
                                From: convertSecondsToDate(convertedOperationHour.From!),
                                To: convertSecondsToDate(convertedOperationHour.To!),
                                _fakeId: convertedOperationHour._fakeId,
                                GeoZoneOperationConfigGracePeriodId:
                                    convertedOperationHour.GeoZoneOperationConfigGracePeriodId,
                            } as any)
                    );
                    day.AllDay = matchingConvertedHours[0].AllDay;
                    day.Status = matchingConvertedHours[0].Status;
                }

                return day;
            });

            reset({
                gracePeriods: _gracePeriods,
                operationHours: _operationHours,
            });
        } else {
            setStartDate(moment().startOf('date'));

            const currentGracePeriods = watch().gracePeriods as Required<GeoZoneGracePeriod>[];
            const updatedGracePeriods = updateGracePeriods({
                currentGracePeriods,
                isZeroGracePeriod: isZeroGracePeriod,
            });

            reset({
                gracePeriods: updatedGracePeriods,
                operationHours: defaultOperationHours,
            });
        }
    }, [props.open, props.gracePeriodConfigs]);

    return (
        <PopUpBase
            open={props.open}
            fixOverflow
            dialogProps={{
                fullWidth: true,
                sx: {
                    '& .MuiDialog-paper': {
                        borderRadius: '10px',
                        minWidth: 900,
                    },
                    zIndex: (theme) => theme.zIndex.modal,
                },
            }}
            onClose={props.onClose}
            onConfirm={() => {}}
            title={
                <Stack>
                    <Typography variant="h4">
                        {props.mode === 'create' ? 'Create Operational Period' : 'Edit Operational Period'}
                    </Typography>
                </Stack>
            }
            titleProps={{ '&.MuiDialogTitle-root': { paddingTop: '24px' } }}
            subTitleProps={{ sx: { color: 'gray' } }}
            minWidthButton={150}
            desc={
                <Stack
                    p={1.7}
                    sx={{
                        border: '1px solid #DDDDDD',
                        borderRadius: 1,
                    }}
                    spacing={2}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={2}
                        sx={{ borderBottom: '1px solid #DDDDDD', pb: 1.7 }}
                    >
                        <Typography>
                            Effective from<span style={{ color: 'red' }}>*</span>:
                        </Typography>
                        <DesktopDatePicker
                            value={startDate}
                            format="DD/MM/YYYY"
                            shouldDisableDate={(date) =>
                                !!props.gracePeriodConfigs?.length && props.mode === 'edit'
                                    ? validateDatePickerPastDate(moment(date).toDate()) !== null
                                    : validateDatePickerValue(moment(date).toDate()) !== null
                            }
                            onChange={(e) => setStartDate(e!)}
                            slotProps={{
                                textField: {
                                    inputProps: {
                                        readOnly: true,
                                    },
                                    sx: {
                                        width: 250,
                                    },
                                },
                            }}
                        />
                    </Stack>
                    <Stack sx={{ borderBottom: '1px solid #DDDDDD', pb: 2 }} spacing={2}>
                        <Typography>
                            Grace period setting<span style={{ color: 'red' }}>*</span>:
                        </Typography>
                        <GracePeriods
                            gracePeriods={gracePeriodForm}
                            errors={errors}
                            control={control}
                            watch={watch}
                            nameWidth={270}
                            contraventionReasonId={props.contraventionReasonId}
                        />
                    </Stack>

                    {operationHours.map((item, index, arr) => {
                        return (
                            <OperationalHours
                                geoZoneHourConfigWithTimeRanges={item as any}
                                geoZoneGracePeriod={watch().gracePeriods}
                                onChange={(item, error) => {
                                    const operationHours = {
                                        Weekday: item.Weekday,
                                        TimeRanges: item.TimeRanges,
                                        AllDay: item.AllDay,
                                        Status: item.Status,
                                    };
                                    updateOperationHours(index, operationHours);
                                    setIsOverlap(error);
                                }}
                            />
                        );
                    })}
                </Stack>
            }
            customActions={
                <Stack direction={'row'} width="100%" justifyContent="space-between">
                    <Button
                        sx={{ minWidth: 130 }}
                        variant="cancel"
                        onClick={() => {
                            if (props.mode == 'edit') {
                                reset({
                                    operationHours: [],
                                    gracePeriods: [defaultGracePeriod],
                                });
                            }
                            props.onClose?.();
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        sx={{ minWidth: 130 }}
                        variant="contained"
                        disabled={
                            !!errors.gracePeriods ||
                            isOverlap ||
                            isInvalidOperationHours ||
                            !isHasInValidGracePeriod ||
                            props.isLoading ||
                            hasOperationHours
                        }
                        onClick={handleSubmit(handleAddOperationHours)}
                    >
                        Save
                    </Button>
                </Stack>
            }
        />
    );
}
